"use client";

/* eslint-disable @typescript-eslint/no-explicit-any */
import { c as _c } from "react/compiler-runtime";
import { CartesianGrid, Label, Legend, Line, LineChart as RechartsLineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import React from "react";
import { AvailableChartColors, type AvailableChartColorsKeys, constructCategoryColors, getColorClassName } from "@/lib/chart-utils";
import { cx } from "@/lib/tailwind-utils";

// Two-palette split so left/right axes have visually distinct color families
const LEFT_COLORS: AvailableChartColorsKeys[] = ["blue", "cyan", "violet"];
const RIGHT_COLORS: AvailableChartColorsKeys[] = ["amber", "pink", "fuchsia", "lime"];
const PALETTE: Record<string, string> = {
  blue: "#3b82f6",
  emerald: "#10b981",
  violet: "#8b5cf6",
  amber: "#f59e0b",
  gray: "#9ca3af",
  cyan: "#06b6d4",
  pink: "#ec4899",
  lime: "#84cc16",
  fuchsia: "#d946ef"
};
interface BiaxialTooltipProps {
  active?: boolean;
  payload?: any[];
  label?: string;
  leftFormatter: (v: number) => string;
  rightFormatter: (v: number) => string;
  rightCategories: string[];
}
const BiaxialTooltip = t0 => {
  const $ = _c(16);
  const {
    active,
    payload,
    label,
    leftFormatter,
    rightFormatter,
    rightCategories
  } = t0;
  if (!active || !payload?.length) {
    return null;
  }
  let t1;
  if ($[0] !== label) {
    t1 = <div className="border-b border-inherit px-4 py-2"><p className="font-medium text-foreground">{label}</p></div>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== leftFormatter || $[3] !== payload || $[4] !== rightCategories || $[5] !== rightFormatter) {
    let t3;
    if ($[7] !== leftFormatter || $[8] !== rightCategories || $[9] !== rightFormatter) {
      t3 = (item, i) => {
        const isRight = rightCategories.includes(item.dataKey);
        const formatted = isRight ? rightFormatter(item.value) : leftFormatter(item.value);
        return <div key={i} className="flex items-center justify-between space-x-8"><div className="flex items-center space-x-2"><span aria-hidden={true} className="size-2 shrink-0 rounded-xs" style={{
              backgroundColor: item.color
            }} /><p className="whitespace-nowrap text-foreground-secondary">{item.name}</p></div><p className="whitespace-nowrap font-medium tabular-nums text-foreground">{formatted}</p></div>;
      };
      $[7] = leftFormatter;
      $[8] = rightCategories;
      $[9] = rightFormatter;
      $[10] = t3;
    } else {
      t3 = $[10];
    }
    t2 = payload.map(t3);
    $[2] = leftFormatter;
    $[3] = payload;
    $[4] = rightCategories;
    $[5] = rightFormatter;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[11] !== t2) {
    t3 = <div className="space-y-1 px-4 py-2">{t2}</div>;
    $[11] = t2;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== t1 || $[14] !== t3) {
    t4 = <div className="rounded-md border border-border bg-background/80 text-body shadow-md backdrop-blur-2xl">{t1}{t3}</div>;
    $[13] = t1;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return t4;
};
export interface BiaxialLineChartProps extends React.HTMLAttributes<HTMLDivElement> {
  data: Record<string, any>[];
  index: string;
  /** Series rendered against the left Y axis */
  leftCategories: string[];
  /** Series rendered against the right Y axis */
  rightCategories: string[];
  leftFormatter?: (value: number) => string;
  rightFormatter?: (value: number) => string;
  leftAxisLabel?: string;
  rightAxisLabel?: string;
  xAxisLabel?: string;
  showGridLines?: boolean;
  showLegend?: boolean;
  yAxisWidth?: number;
  animated?: boolean;
}
const BiaxialLineChart = React.forwardRef<HTMLDivElement, BiaxialLineChartProps>(({
  data = [],
  index,
  leftCategories = [],
  rightCategories = [],
  leftFormatter = v => v.toLocaleString(),
  rightFormatter = v_0 => v_0.toLocaleString(),
  leftAxisLabel,
  rightAxisLabel,
  xAxisLabel,
  showGridLines = true,
  showLegend = true,
  yAxisWidth = 56,
  animated = true,
  className,
  ...other
}, forwardedRef) => {
  const allCategories = [...leftCategories, ...rightCategories];
  const leftColors = leftCategories.map((_, i) => LEFT_COLORS[i % LEFT_COLORS.length]);
  const rightColors = rightCategories.map((__0, i_0) => RIGHT_COLORS[i_0 % RIGHT_COLORS.length]);
  const categoryColors = constructCategoryColors(allCategories, AvailableChartColors);
  const getColor = (cat: string): string => {
    const key = categoryColors.get(cat) as AvailableChartColorsKeys;
    return PALETTE[key] ?? "#3b82f6";
  };
  return <div ref={forwardedRef} className={cx("h-80 w-full", className)} tremor-id="tremor-raw" {...other}>
        <ResponsiveContainer>
          <RechartsLineChart data={data} margin={{
        bottom: xAxisLabel ? 30 : undefined,
        left: leftAxisLabel ? 20 : undefined,
        right: rightAxisLabel ? 40 : 20,
        top: leftAxisLabel || rightAxisLabel ? 15 : 5
      }}>
            {showGridLines && <CartesianGrid className="stroke-border stroke-1" vertical={false} />}
            <XAxis dataKey={index} tick={{
          className: "text-caption fill-muted-foreground",
          transform: "translate(0, 6)"
        }} fill="" stroke="" tickLine={false} axisLine={false} minTickGap={5}>
              {xAxisLabel && <Label position="insideBottom" offset={-20} className="fill-foreground text-body-strong">
                  {xAxisLabel}
                </Label>}
            </XAxis>
            {/* Left Y axis */}
            <YAxis yAxisId="left" width={yAxisWidth} axisLine={false} tickLine={false} fill="" stroke="" tick={{
          className: "text-caption fill-muted-foreground",
          transform: "translate(-3, 0)"
        }} tickFormatter={leftFormatter}>
              {leftAxisLabel && <Label position="insideLeft" style={{
            textAnchor: "middle"
          }} angle={-90} offset={-15} className="fill-foreground text-body-strong">
                  {leftAxisLabel}
                </Label>}
            </YAxis>
            {/* Right Y axis */}
            <YAxis yAxisId="right" orientation="right" width={yAxisWidth} axisLine={false} tickLine={false} fill="" stroke="" tick={{
          className: "text-caption fill-muted-foreground",
          transform: "translate(3, 0)"
        }} tickFormatter={rightFormatter}>
              {rightAxisLabel && <Label position="insideRight" style={{
            textAnchor: "middle"
          }} angle={90} offset={15} className="fill-foreground text-body-strong">
                  {rightAxisLabel}
                </Label>}
            </YAxis>
            <Tooltip wrapperStyle={{
          outline: "none",
          zIndex: 10
        }} isAnimationActive animationDuration={100} cursor={{
          stroke: "var(--border)",
          strokeWidth: 1,
          opacity: 0.5
        }} content={({
          active,
          payload,
          label
        }) => <BiaxialTooltip active={active} payload={payload ? [...payload] : []} label={label === undefined ? undefined : String(label)} leftFormatter={leftFormatter} rightFormatter={rightFormatter} rightCategories={rightCategories} />} />
            {showLegend && <Legend verticalAlign="top" wrapperStyle={{
          paddingBottom: 16,
          color: "var(--foreground)"
        }} />}
            {leftCategories.map((cat_0, i_1) => <Line key={cat_0} yAxisId="left" type="monotone" dataKey={cat_0} stroke={PALETTE[leftColors[i_1]] ?? "#3b82f6"} strokeWidth={2} dot={false} className={cx(getColorClassName(categoryColors.get(cat_0) as AvailableChartColorsKeys, "stroke"))} isAnimationActive={animated} animationDuration={animated ? 600 : 0} />)}
            {rightCategories.map((cat_1, i_2) => <Line key={cat_1} yAxisId="right" type="monotone" dataKey={cat_1} stroke={PALETTE[rightColors[i_2]] ?? "#f59e0b"} strokeWidth={2} dot={false} strokeDasharray="4 2" className={cx(getColorClassName(categoryColors.get(cat_1) as AvailableChartColorsKeys, "stroke"))} isAnimationActive={animated} animationDuration={animated ? 600 : 0} />)}
          </RechartsLineChart>
        </ResponsiveContainer>
      </div>;
});
BiaxialLineChart.displayName = "BiaxialLineChart";
export { BiaxialLineChart };